// src/DemoAdmin.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import axios from 'axios';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  AppBar,
  Toolbar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Divider,
  FormControlLabel,
  Checkbox,
  TextField,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Analytics as AnalyticsIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  Dashboard as DashboardIcon,
  Visibility as VisibilityIcon,
  QuestionAnswer as QuestionAnswerIcon,
  TrendingUp as TrendingUpIcon,
  Language as LanguageIcon,
  Queue as QueueIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
  SmartToy as SmartToyIcon,
} from '@mui/icons-material';
import UserMenu from './components/UserMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const DEMO_DOMAIN = process.env.REACT_APP_DEMO_DOMAIN || 'https://demo.comturk.com';

function DemoAdmin({ handleLogout }) {
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Users state (admin selects which user to create demo for)
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');

  // Teams state
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [generateAIContent, setGenerateAIContent] = useState(true);

  // Demos state
  const [demos, setDemos] = useState([]);
  const [loadingDemos, setLoadingDemos] = useState(false);

  // Dashboard stats
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Creation state
  const [isCreating, setIsCreating] = useState(false);

  // Analytics dialog
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [selectedAnalytics, setSelectedAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [demoToDelete, setDemoToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Create from Domain state
  const [domainUrl, setDomainUrl] = useState('');
  const [maxPagesToCrawl, setMaxPagesToCrawl] = useState(10);
  const [systemDefaultMaxPages, setSystemDefaultMaxPages] = useState(10);
  const [isCreatingFromDomain, setIsCreatingFromDomain] = useState(false);
  const [isSavingDefault, setIsSavingDefault] = useState(false);

  // Queue status state
  const [queueStatus, setQueueStatus] = useState({ active_demos: 0, max_concurrent: 3, queued_count: 0 });

  // Sessions dialog state
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [sessionsDemo, setSessionsDemo] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [expandedSession, setExpandedSession] = useState(null);

  // Get auth token
  const getAuthToken = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token;
  }, []);

  // Fetch users (admin functionality)
  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    setError('');

    try {
      const token = await getAuthToken();
      const anonKey = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY;

      const response = await fetch(`${BACKEND_URL}/functions/v1/admin-user-manager/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'apikey': anonKey,
        }
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch users');
      }

      setUsers(data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users: ' + err.message);
    } finally {
      setLoadingUsers(false);
    }
  }, [getAuthToken]);

  // Fetch available teams for selected user
  const fetchTeams = useCallback(async (userId) => {
    if (!userId) {
      setTeams([]);
      return;
    }
    setLoadingTeams(true);
    setError('');

    try {
      const token = await getAuthToken();
      const response = await axios.get(`${BACKEND_URL}/api/demos/teams/available`, {
        params: { target_user_id: userId },
        headers: { Authorization: `Bearer ${token}` }
      });

      setTeams(response.data.teams || []);
    } catch (err) {
      console.error('Error fetching teams:', err);
      setError('Failed to load teams: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoadingTeams(false);
    }
  }, [getAuthToken]);

  // Fetch demos
  const fetchDemos = useCallback(async () => {
    setLoadingDemos(true);
    setError('');

    try {
      const token = await getAuthToken();
      const response = await axios.get(`${BACKEND_URL}/api/demos/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setDemos(response.data.demos || []);
    } catch (err) {
      console.error('Error fetching demos:', err);
      setError('Failed to load demos: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoadingDemos(false);
    }
  }, [getAuthToken]);

  // Fetch dashboard stats
  const fetchStats = useCallback(async () => {
    setLoadingStats(true);

    try {
      const token = await getAuthToken();
      const response = await axios.get(`${BACKEND_URL}/api/demos/dashboard-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setStats(response.data.stats || {});
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoadingStats(false);
    }
  }, [getAuthToken]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchUsers(),
        fetchDemos(),
        fetchStats()
      ]);
      setLoading(false);
    };

    loadData();
  }, [fetchUsers, fetchDemos, fetchStats]);

  // When user is selected, fetch their teams
  useEffect(() => {
    if (selectedUserId) {
      fetchTeams(selectedUserId);
      setSelectedTeamId(''); // Reset team selection when user changes
    } else {
      setTeams([]);
      setSelectedTeamId('');
    }
  }, [selectedUserId, fetchTeams]);

  // Handle create demo
  const handleCreateDemo = async () => {
    if (!selectedUserId) {
      setError('Please select a user first');
      return;
    }

    if (!selectedTeamId) {
      setError('Please select a team');
      return;
    }

    const selectedTeam = teams.find(t => t.team_id === selectedTeamId);
    if (!selectedTeam) {
      setError('Selected team not found');
      return;
    }

    if (selectedTeam.has_demo) {
      setError(`Team "${selectedTeam.team_name}" already has a demo: /demo/${selectedTeam.existing_demo_slug}`);
      return;
    }

    if (selectedTeam.file_count === 0) {
      setError('Selected team has no files. Please crawl and upload files first.');
      return;
    }

    const selectedUser = users.find(u => u.id === selectedUserId);
    const userName = selectedUser?.email || 'selected user';

    const confirmMsg = `Create demo for "${selectedTeam.team_name}" (User: ${userName})?\n\nThis will:\n- Create Chatwoot bot and inbox\n- Generate demo page\n- Use ${selectedTeam.file_count} files for RAG\n${generateAIContent ? '- Generate AI-personalized content' : ''}`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    setIsCreating(true);
    setError('');
    setMessage('');

    try {
      const token = await getAuthToken();
      const response = await axios.post(
        `${BACKEND_URL}/api/demos/create`,
        {
          team_id: selectedTeamId,
          team_name: selectedTeam.team_name,
          generate_ai_content: generateAIContent,
          target_user_id: selectedUserId
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setMessage(`Demo created successfully for ${userName}! URL: ${response.data.demo_url}`);
      setSelectedTeamId('');

      // Refresh data
      await Promise.all([
        fetchTeams(selectedUserId),
        fetchDemos(),
        fetchStats()
      ]);

    } catch (err) {
      console.error('Error creating demo:', err);
      setError('Failed to create demo: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsCreating(false);
    }
  };

  // Handle view analytics
  const handleViewAnalytics = async (demoId) => {
    setLoadingAnalytics(true);
    setAnalyticsOpen(true);
    setSelectedAnalytics(null);

    try {
      const token = await getAuthToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/demos/${demoId}/analytics`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSelectedAnalytics(response.data.analytics);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics: ' + (err.response?.data?.detail || err.message));
      setAnalyticsOpen(false);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Handle view sessions (chat messages)
  const handleViewSessions = async (demo) => {
    setLoadingSessions(true);
    setSessionsOpen(true);
    setSelectedSessions([]);
    setSessionsDemo(demo);
    setExpandedSession(null);

    try {
      const token = await getAuthToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/demos/${demo.demo_id}/sessions`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSelectedSessions(response.data.sessions || []);
    } catch (err) {
      console.error('Error fetching sessions:', err);
      setError('Failed to load sessions: ' + (err.response?.data?.detail || err.message));
      setSessionsOpen(false);
    } finally {
      setLoadingSessions(false);
    }
  };

  // Handle delete demo
  const handleDeleteClick = (demo) => {
    setDemoToDelete(demo);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!demoToDelete) return;

    setIsDeleting(true);

    try {
      const token = await getAuthToken();
      const response = await axios.delete(
        `${BACKEND_URL}/api/demos/${demoToDelete.demo_id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Show detailed success message
      const details = response.data?.details;
      let successMsg = 'Demo deleted successfully';
      if (details?.chatwoot_cleanup) {
        const cleanup = details.chatwoot_cleanup;
        if (cleanup.bot || cleanup.inbox) {
          successMsg += ' (Chatwoot ';
          const cleaned = [];
          if (cleanup.bot) cleaned.push('bot');
          if (cleanup.inbox) cleaned.push('inbox');
          successMsg += cleaned.join(' & ') + ' cleaned up)';
        }
      }

      setMessage(successMsg);
      setDeleteDialogOpen(false);
      setDemoToDelete(null);

      // Refresh data
      await Promise.all([
        fetchTeams(),
        fetchDemos(),
        fetchStats()
      ]);

    } catch (err) {
      console.error('Error deleting demo:', err);
      setError('Failed to delete demo: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    setMessage('');
    setError('');
    await Promise.all([
      fetchTeams(),
      fetchDemos(),
      fetchStats()
    ]);
  };

  // Fetch demo settings on load
  const fetchDemoSettings = useCallback(async () => {
    try {
      const apiKey = process.env.REACT_APP_DEMO_AUTOMATION_API_KEY || 'J3AfDpg47FGo-GVhe0aIq4NLO4yJPoYjvtjP6kNpMlI';
      const response = await axios.get(
        `${BACKEND_URL}/api/demo-automation/settings`,
        { headers: { 'X-API-Key': apiKey } }
      );
      const defaultPages = response.data.demo_max_pages || 10;
      setSystemDefaultMaxPages(defaultPages);
      setMaxPagesToCrawl(defaultPages);
    } catch (err) {
      console.error('Error fetching demo settings:', err);
      // Use defaults on error
    }
  }, []);

  // Fetch queue status
  const fetchQueueStatus = useCallback(async () => {
    try {
      const apiKey = process.env.REACT_APP_DEMO_AUTOMATION_API_KEY || 'J3AfDpg47FGo-GVhe0aIq4NLO4yJPoYjvtjP6kNpMlI';
      const response = await axios.get(
        `${BACKEND_URL}/api/demo-automation/queue-status`,
        { headers: { 'X-API-Key': apiKey } }
      );
      setQueueStatus(response.data);
    } catch (err) {
      console.error('Error fetching queue status:', err);
    }
  }, []);

  useEffect(() => {
    fetchDemoSettings();
    fetchQueueStatus();
    // Poll queue status every 10 seconds
    const interval = setInterval(fetchQueueStatus, 10000);
    return () => clearInterval(interval);
  }, [fetchDemoSettings, fetchQueueStatus]);

  // Save system default max pages
  const handleSaveDefaultMaxPages = async () => {
    setIsSavingDefault(true);
    setError('');

    try {
      const apiKey = process.env.REACT_APP_DEMO_AUTOMATION_API_KEY || 'J3AfDpg47FGo-GVhe0aIq4NLO4yJPoYjvtjP6kNpMlI';
      await axios.put(
        `${BACKEND_URL}/api/demo-automation/settings`,
        { demo_max_pages: systemDefaultMaxPages },
        { headers: { 'X-API-Key': apiKey, 'Content-Type': 'application/json' } }
      );
      setMessage(`Default max pages saved: ${systemDefaultMaxPages}`);
      setMaxPagesToCrawl(systemDefaultMaxPages);
    } catch (err) {
      console.error('Error saving demo settings:', err);
      setError('Failed to save default: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsSavingDefault(false);
    }
  };

  // Create demo from domain URL
  const handleCreateFromDomain = async () => {
    if (!domainUrl) {
      setError('Please enter a website URL');
      return;
    }

    if (!window.confirm(`Create demo for "${domainUrl}"?\n\nThis will crawl up to ${maxPagesToCrawl} pages and may take 2-5 minutes.`)) {
      return;
    }

    setIsCreatingFromDomain(true);
    setError('');
    setMessage('');

    try {
      const apiKey = process.env.REACT_APP_DEMO_AUTOMATION_API_KEY || 'J3AfDpg47FGo-GVhe0aIq4NLO4yJPoYjvtjP6kNpMlI';
      const response = await axios.post(
        `${BACKEND_URL}/api/demo-automation/create-from-url`,
        { website_url: domainUrl, max_pages: maxPagesToCrawl },
        { headers: { 'X-API-Key': apiKey, 'Content-Type': 'application/json' } }
      );

      if (response.data.existing) {
        setMessage(`Demo already exists: ${response.data.demo_url}`);
      } else {
        setMessage(`Demo created successfully! URL: ${response.data.demo_url}`);
      }

      setDomainUrl('');

      // Refresh demos list
      await Promise.all([fetchDemos(), fetchStats()]);

    } catch (err) {
      console.error('Error creating demo from domain:', err);
      setError('Failed to create demo: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsCreatingFromDomain(false);
    }
  };

  const selectedTeam = teams.find(t => t.team_id === selectedTeamId);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* AppBar */}
      <AppBar position="static" sx={{ background: '#fff', borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar variant="dense">
          <Tooltip title="Back to Chat">
            <IconButton
              sx={{ mr: 1, color: '#000' }}
              onClick={() => navigate('/')}
              edge="start"
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>

          <DashboardIcon sx={{ mr: 1, color: '#1976d2' }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: '#000' }}>
            Demo Management Dashboard
          </Typography>

          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} sx={{ color: '#000', mr: 1 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <UserMenu
            onLogout={handleLogout}
            onOpenSettings={() => { }}
            onOpenProfile={() => { }}
          />
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, flexGrow: 1, overflow: 'auto' }}>
        {/* Success/Error Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {message && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setMessage('')}>
            {message}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && (
          <>
            {/* Dashboard Statistics */}
            {stats && (
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: '#e3f2fd' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <DashboardIcon sx={{ mr: 1, color: '#1976d2' }} />
                        <Typography variant="subtitle2" color="text.secondary">
                          Total Demos
                        </Typography>
                      </Box>
                      <Typography variant="h4">
                        {stats.total_demos || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stats.active_demos || 0} active
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: '#f3e5f5' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <VisibilityIcon sx={{ mr: 1, color: '#9c27b0' }} />
                        <Typography variant="subtitle2" color="text.secondary">
                          Total Views
                        </Typography>
                      </Box>
                      <Typography variant="h4">
                        {stats.total_views || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: '#e8f5e9' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <QuestionAnswerIcon sx={{ mr: 1, color: '#4caf50' }} />
                        <Typography variant="subtitle2" color="text.secondary">
                          Total Questions
                        </Typography>
                      </Box>
                      <Typography variant="h4">
                        {stats.total_questions || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: '#fff3e0' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <TrendingUpIcon sx={{ mr: 1, color: '#ff9800' }} />
                        <Typography variant="subtitle2" color="text.secondary">
                          Most Active
                        </Typography>
                      </Box>
                      <Typography variant="h6" noWrap>
                        {stats.most_active_demo_name || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stats.most_active_demo_questions || 0} questions
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: '#fce4ec' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <QueueIcon sx={{ mr: 1, color: '#e91e63' }} />
                        <Typography variant="subtitle2" color="text.secondary">
                          Demo Queue
                        </Typography>
                      </Box>
                      <Typography variant="h4">
                        {queueStatus.active_demos}/{queueStatus.max_concurrent}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {queueStatus.queued_count > 0
                          ? `${queueStatus.queued_count} queued (~${queueStatus.queued_count * 3} min wait)`
                          : 'No queue'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* Create Demo Section */}
            <Paper sx={{ p: 3, mb: 4, bgcolor: '#f5f5f5' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <AddIcon sx={{ mr: 1 }} />
                Create New Demo
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2} alignItems="center">
                {/* User Selection (Step 1) */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Select User</InputLabel>
                    <Select
                      value={selectedUserId}
                      label="Select User"
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      disabled={loadingUsers || isCreating}
                    >
                      <MenuItem value="">
                        <em>-- Select a user --</em>
                      </MenuItem>
                      {users.map(user => (
                        <MenuItem
                          key={user.id}
                          value={user.id}
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body2">{user.email}</Typography>
                            {user.raw_user_meta_data?.full_name && (
                              <Typography variant="caption" color="text.secondary">
                                {user.raw_user_meta_data.full_name}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {loadingUsers && (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        <Typography variant="caption">Loading users...</Typography>
                      </Box>
                    )}
                  </FormControl>
                </Grid>

                {/* Team Selection (Step 2) */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Select Team</InputLabel>
                    <Select
                      value={selectedTeamId}
                      label="Select Team"
                      onChange={(e) => setSelectedTeamId(e.target.value)}
                      disabled={!selectedUserId || loadingTeams || isCreating}
                    >
                      <MenuItem value="">
                        <em>-- Select a team --</em>
                      </MenuItem>
                      {teams.map(team => (
                        <MenuItem
                          key={team.team_id}
                          value={team.team_id}
                          disabled={team.has_demo}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                            <span>{team.team_name}</span>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Chip
                                label={`${team.file_count} files`}
                                size="small"
                                variant="outlined"
                                color={team.file_count > 0 ? 'success' : 'default'}
                              />
                              {team.has_demo && (
                                <Chip
                                  label="Has Demo"
                                  size="small"
                                  color="warning"
                                />
                              )}
                            </Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {!selectedUserId && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="warning.main">
                        ⚠️ Please select a user first
                      </Typography>
                    </Box>
                  )}

                  {selectedTeam && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {selectedTeam.file_count} files available for RAG
                        {selectedTeam.has_demo && ` • Demo exists: /demo/${selectedTeam.existing_demo_slug}`}
                      </Typography>
                    </Box>
                  )}

                  {loadingTeams && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      <Typography variant="caption">Loading teams...</Typography>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={generateAIContent}
                        onChange={(e) => setGenerateAIContent(e.target.checked)}
                        disabled={isCreating}
                      />
                    }
                    label="Generate AI Content"
                  />
                  <Typography variant="caption" display="block" color="text.secondary">
                    Use homepage to personalize
                  </Typography>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={isCreating ? <CircularProgress size={20} /> : <AddIcon />}
                    onClick={handleCreateDemo}
                    disabled={!selectedUserId || !selectedTeamId || isCreating || loadingTeams}
                  >
                    {isCreating ? 'Creating...' : 'Create Demo'}
                  </Button>
                </Grid>
              </Grid>

              {/* Helper Instructions */}
              <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>How to create a demo:</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  1. Select a user from the dropdown<br />
                  2. Select one of their teams<br />
                  3. Click "Create Demo" to generate a personalized demo page
                </Typography>
              </Box>
            </Paper>

            {/* Create Demo from Domain Section */}
            <Paper sx={{ p: 3, mb: 4, bgcolor: '#e3f2fd' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <LanguageIcon sx={{ mr: 1 }} />
                Create Demo from Website URL
              </Typography>

              <Divider sx={{ my: 2 }} />

              {/* System Default Settings Row */}
              <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      label="System Default Max Pages"
                      type="number"
                      size="small"
                      value={systemDefaultMaxPages}
                      onChange={(e) => setSystemDefaultMaxPages(Math.max(1, Math.min(100, parseInt(e.target.value) || 10)))}
                      inputProps={{ min: 1, max: 100 }}
                      sx={{ width: 200 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleSaveDefaultMaxPages}
                      disabled={isSavingDefault}
                    >
                      {isSavingDefault ? 'Saving...' : 'Save Default'}
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Used when n8n/AI creates demos automatically
                  </Typography>
                </Grid>
              </Grid>

              {/* Create from Domain Row */}
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    label="Website URL"
                    placeholder="https://example.com"
                    value={domainUrl}
                    onChange={(e) => setDomainUrl(e.target.value)}
                    disabled={isCreatingFromDomain}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Pages to Crawl"
                    type="number"
                    value={maxPagesToCrawl}
                    onChange={(e) => setMaxPagesToCrawl(Math.max(1, Math.min(100, parseInt(e.target.value) || 10)))}
                    inputProps={{ min: 1, max: 100 }}
                    disabled={isCreatingFromDomain}
                    helperText="Override for this demo only"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Button
                    variant="contained"
                    fullWidth
                    color="primary"
                    startIcon={isCreatingFromDomain ? <CircularProgress size={20} color="inherit" /> : <LanguageIcon />}
                    onClick={handleCreateFromDomain}
                    disabled={!domainUrl || isCreatingFromDomain}
                  >
                    {isCreatingFromDomain ? 'Creating Demo...' : 'Create from URL'}
                  </Button>
                </Grid>
              </Grid>

              {isCreatingFromDomain && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
                  <Typography variant="body2" color="warning.main">
                    ⏳ Crawling website and creating demo... This may take 2-5 minutes.
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Demos List */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>
                  <DashboardIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Active Demos ({demos.length})
                </span>
              </Typography>

              <Divider sx={{ my: 2 }} />

              {loadingDemos && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {!loadingDemos && demos.length === 0 && (
                <Box sx={{ textAlign: 'center', p: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No demos created yet. Create your first demo above!
                  </Typography>
                </Box>
              )}

              {!loadingDemos && demos.length > 0 && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Team Name</strong></TableCell>
                        <TableCell><strong>Slug</strong></TableCell>
                        <TableCell><strong>Website</strong></TableCell>
                        <TableCell align="center"><strong>Views</strong></TableCell>
                        <TableCell align="center"><strong>Sessions</strong></TableCell>
                        <TableCell align="center"><strong>Messages</strong></TableCell>
                        <TableCell><strong>Last Activity</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell align="center"><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {demos.map((demo) => (
                        <TableRow key={demo.demo_id} hover>
                          <TableCell>{demo.team_name}</TableCell>
                          <TableCell>
                            <code>/demo/{demo.slug}</code>
                          </TableCell>
                          <TableCell>
                            {demo.website_url ? (
                              <Tooltip title={demo.website_url}>
                                <a href={demo.website_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                                  {(() => { try { return new URL(demo.website_url).hostname; } catch { return demo.website_url; } })()}
                                </a>
                              </Tooltip>
                            ) : '-'}
                          </TableCell>
                          <TableCell align="center">{demo.view_count || 0}</TableCell>
                          <TableCell align="center">
                            {(demo.session_count || 0) > 0 ? (
                              <Tooltip title="Click to view chat sessions">
                                <Button
                                  size="small"
                                  onClick={() => handleViewSessions(demo)}
                                  sx={{ minWidth: 30, color: '#1976d2', fontWeight: 'bold' }}
                                >
                                  {demo.session_count}
                                </Button>
                              </Tooltip>
                            ) : (
                              0
                            )}
                          </TableCell>
                          <TableCell align="center">{demo.message_count || 0}</TableCell>
                          <TableCell>
                            {demo.last_activity ? new Date(demo.last_activity).toLocaleString() : 'Never'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={demo.is_active ? 'Active' : 'Inactive'}
                              size="small"
                              color={demo.is_active ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Tooltip title="View Demo Page">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => window.open(`${DEMO_DOMAIN}/demo/${demo.slug}`, '_blank')}
                                >
                                  <OpenInNewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="View Analytics">
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => handleViewAnalytics(demo.demo_id)}
                                >
                                  <AnalyticsIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Delete Demo">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteClick(demo)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </>
        )}
      </Container>

      {/* Analytics Dialog */}
      <Dialog
        open={analyticsOpen}
        onClose={() => setAnalyticsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Demo Analytics</DialogTitle>
        <DialogContent>
          {loadingAnalytics && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loadingAnalytics && selectedAnalytics && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="subtitle2" color="text.secondary">Team Name</Typography>
                  <Typography variant="h6">{selectedAnalytics.team_name}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="subtitle2" color="text.secondary">Slug</Typography>
                  <Typography variant="h6">/demo/{selectedAnalytics.slug}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                  <Typography variant="subtitle2" color="text.secondary">Total Views</Typography>
                  <Typography variant="h4">{selectedAnalytics.view_count}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, bgcolor: '#e8f5e9' }}>
                  <Typography variant="subtitle2" color="text.secondary">Total Questions</Typography>
                  <Typography variant="h4">{selectedAnalytics.question_count}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, bgcolor: '#fff3e0' }}>
                  <Typography variant="subtitle2" color="text.secondary">Unique Conversations</Typography>
                  <Typography variant="h4">{selectedAnalytics.unique_conversations}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, bgcolor: '#f3e5f5' }}>
                  <Typography variant="subtitle2" color="text.secondary">Avg Questions/Session</Typography>
                  <Typography variant="h4">{selectedAnalytics.avg_questions_per_session}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="subtitle2" color="text.secondary">Last Activity</Typography>
                  <Typography variant="body1">
                    {selectedAnalytics.last_activity
                      ? new Date(selectedAnalytics.last_activity).toLocaleString()
                      : 'No activity yet'}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="subtitle2" color="text.secondary">Created</Typography>
                  <Typography variant="body1">
                    {new Date(selectedAnalytics.created_at).toLocaleString()}
                    {' '}({selectedAnalytics.days_active} days ago)
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalyticsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !isDeleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Demo?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this demo?
          </Typography>
          {demoToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Team:</strong> {demoToDelete.team_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>URL:</strong> /demo/{demoToDelete.slug}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Views:</strong> {demoToDelete.view_count} | <strong>Questions:</strong> {demoToDelete.question_count}
              </Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Warning:</strong> This will permanently delete:
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>The demo page</li>
              <li>Chatwoot bot and inbox</li>
              <li>Integration configuration</li>
              <li>All analytics data</li>
            </ul>
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete Demo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sessions Dialog - View Chat Messages */}
      <Dialog
        open={sessionsOpen}
        onClose={() => setSessionsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChatIcon color="primary" />
            Chat Sessions - {sessionsDemo?.team_name || 'Demo'}
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingSessions && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loadingSessions && selectedSessions.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">No chat sessions found</Typography>
            </Box>
          )}

          {!loadingSessions && selectedSessions.length > 0 && (
            <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
              {selectedSessions.map((session, idx) => (
                <Paper
                  key={session.id}
                  sx={{ mb: 2, p: 2, bgcolor: expandedSession === session.id ? '#f0f7ff' : '#f5f5f5', cursor: 'pointer' }}
                  onClick={() => setExpandedSession(expandedSession === session.id ? null : session.id)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2">
                        Session #{idx + 1} - {session.visitor_name || session.visitor_id?.slice(0, 8) || 'Anonymous'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(session.created_at).toLocaleString()} • {session.messages?.length || 0} messages
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={expandedSession === session.id ? 'Collapse' : 'Expand'}
                      color={expandedSession === session.id ? 'primary' : 'default'}
                    />
                  </Box>

                  {expandedSession === session.id && session.messages && session.messages.length > 0 && (
                    <Box sx={{ mt: 2, pl: 2, borderLeft: '3px solid #1976d2' }}>
                      {session.messages.map((msg, msgIdx) => (
                        <Box
                          key={msg.id || msgIdx}
                          sx={{
                            mb: 1.5,
                            p: 1.5,
                            bgcolor: msg.role === 'user' ? '#e3f2fd' : '#f5f5f5',
                            borderRadius: 1,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            {msg.role === 'user' ? (
                              <PersonIcon fontSize="small" color="primary" />
                            ) : (
                              <SmartToyIcon fontSize="small" color="action" />
                            )}
                            <Typography variant="caption" fontWeight="bold">
                              {msg.role === 'user' ? 'Visitor' : 'AI'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(msg.created_at).toLocaleTimeString()}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {msg.content}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSessionsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DemoAdmin;

